import { Children, ComponentProps, PropsWithChildren, ReactNode } from "react";
import { Swiper, SwiperSlide, SwiperProps } from "swiper/react";
import type { SwiperOptions as SlideOptions } from "swiper/types";
import { Autoplay, EffectFade, Navigation, Pagination } from "swiper/modules";
import { ChevronLeftOutline, ChevronRightOutline } from "@spoctech/icons";
import { merge } from "@/utils/helpers";
import Button from "../Button";
import Box from "../Box";
import Typography from "../Typography";
import {
  bottomNavigationButtonClasses,
  navigationIconClasses,
  slideClasses,
  sliderActiveBulletsClasses,
  sliderBulletsClasses,
  sliderClasses,
  sliderPaginationClasses,
  SliderPaginationVariants,
  sliderRootClasses,
  SliderRootVariants,
  sliderTextContentClasses,
  SliderVariants,
  wrapperClasses,
} from "./Slider.style";

// Import Swiper styles
import "swiper/css";
import "swiper/css/effect-fade";

export type SliderProps = SliderVariants &
  SliderRootVariants &
  SliderPaginationVariants & {
    id: string;
    options: SlideOptions;
    heading?: ReactNode;
    subHeading?: ReactNode;
    paragraph?: ReactNode;
    render?: ComponentProps<typeof Box>["render"];
    navigationCentered?: boolean;
    navigationCenteredInner?: boolean;
    removeContainerPadding?: boolean;
    removeSliderPadding?: boolean;
    hideNavigation?: boolean;
    swiperProps?: SwiperProps;
  };

const Slider = (props: PropsWithChildren<SliderProps>) => {
  const {
    children,
    id,
    options = {},
    paginationPosition,
    paginationOverlay,
    heading,
    subHeading,
    paragraph,
    render = <div />,
    navigationCentered,
    navigationCenteredInner,
    removeContainerPadding,
    removeSliderPadding,
    hideNavigation = false,
    swiperProps,
  } = props;

  const sliderChildren = Children.map(children, (child, index) => (
    <SwiperSlide className={slideClasses({})} key={index}>
      {child}
    </SwiperSlide>
  ));

  const defaultOptions = {
    wrapperClass: wrapperClasses({}),
    navigation: {
      enabled: false,
      nextEl: `.${id}-next`,
      prevEl: `.${id}-prev`,
    },
    pagination: {
      el: `.${id}-pagination`,
      type: "bullets",
      bulletActiveClass: sliderActiveBulletsClasses(),
      bulletClass: sliderBulletsClasses(),
      clickable: true,
    },
  } satisfies SlideOptions;

  const mergedOptions = {
    ...merge(defaultOptions, options),
  } satisfies SlideOptions;

  const isTextContent =
    Boolean(heading) || Boolean(subHeading) || Boolean(paragraph);
  const isTopNavigation =
    !hideNavigation &&
    mergedOptions?.navigation?.enabled &&
    !navigationCentered;
  return (
    <Box
      render={render}
      className={sliderRootClasses({
        removeContainerPadding,
      })}
    >
      {isTextContent ||
        (isTopNavigation && (
          <Box
            className={sliderTextContentClasses({
              isTextContent,
              isMargin:
                mergedOptions?.navigation?.enabled && !navigationCentered,
            })}
          >
            {(heading || subHeading) && (
              <Box className="flex-1">
                {Boolean(heading) && (
                  <Typography variant="h2" gutterBottom>
                    {heading}
                  </Typography>
                )}
                {Boolean(subHeading) && (
                  <Typography variant="h5" gutterBottom>
                    {subHeading}
                  </Typography>
                )}
                {Boolean(paragraph) && (
                  <Typography variant="body1">{paragraph}</Typography>
                )}
              </Box>
            )}

            {!hideNavigation &&
              mergedOptions?.navigation?.enabled &&
              !navigationCentered && (
                <Box className="flex gap-2">
                  <Button
                    color="primary"
                    className={`${id}-prev `}
                    icon={<ChevronLeftOutline className={"w-5 h-5"} />}
                  />
                  <Button
                    color="primary"
                    className={`${id}-next`}
                    icon={<ChevronRightOutline className={"w-5 h-5"} />}
                  />
                </Box>
              )}
          </Box>
        ))}
      {/* Slider Relative Area */}
      <Box className="relative">
        <Swiper
          modules={[Navigation, Pagination, EffectFade, Autoplay]}
          {...mergedOptions}
          className={sliderClasses({
            removeSliderPadding,
          })}
          {...swiperProps}
        >
          {sliderChildren}
        </Swiper>
        {defaultOptions.pagination.type === "bullets" &&
          options?.pagination && (
            <Box
              className={sliderPaginationClasses({
                class: `${id}-pagination`,
                paginationPosition,
                paginationOverlay,
              })}
            />
          )}
        {!hideNavigation &&
          mergedOptions?.navigation?.enabled &&
          navigationCentered && (
            <>
              <Button
                color={"primary"}
                className={bottomNavigationButtonClasses({
                  class: `${id}-prev`,
                  left: true,
                  isCentered: paginationOverlay,
                  isInner: navigationCenteredInner,
                })}
                icon={
                  <ChevronLeftOutline
                    className={navigationIconClasses({
                      isInner: navigationCenteredInner,
                    })}
                  />
                }
              />
              <Button
                color={"primary"}
                className={bottomNavigationButtonClasses({
                  class: `${id}-next`,
                  right: true,
                  isCentered: paginationOverlay,
                  isInner: navigationCenteredInner,
                })}
                icon={
                  <ChevronRightOutline
                    className={navigationIconClasses({
                      isInner: navigationCenteredInner,
                    })}
                  />
                }
              />
            </>
          )}
      </Box>
    </Box>
  );
};
export default Slider;
